import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'

interface ExamsTabProps {
  patientId: string
}

interface ExamRow {
  _id: string
  date?: string
  type?: string
  requestingUser?: string
  reportingDoctor?: string
  state?: string
  unsignedReportId?: string
  signedReportId?: string
  studyUID?: string
  [key: string]: any
}

interface ExamColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: ExamColumnConfig[] = [
  {id: 'date', label: 'DATA ESAME'},
  {id: 'type', label: 'TIPO ESAME'},
  {id: 'requestingUser', label: 'MEDICO RICHIEDENTE'},
  {id: 'reportingDoctor', label: 'MEDICO REFERTANTE'},
  {id: 'state', label: 'STATO REFERTO'},
]

const REPORT_STATUS_OPTIONS = [
  {value: 'NOT_COMPLETED', label: 'Assente'},
  {value: 'COMPLETED', label: 'Completato'},
]

// Each filter mirrors a column (columnId) but doesn't share its id, since a filter can target
// a differently-shaped value (e.g. rangeDates -> the single "date" column). A filter is only
// shown when its mirrored column is present in columnConfig, so disabling a column via PSP
// config also hides the filter that would otherwise let users query a field they can't see.
const ALL_FILTERS: Array<FilterItem & {columnId: string}> = [
  {id: 'type', columnId: 'type', label: 'Tipo esame', type: 'select', options: [{value: 'eco', label: 'ECO'}]},
  {id: 'rangeDates', columnId: 'date', label: 'Data', type: 'rangePicker'},
  {id: 'reportStatus', columnId: 'state', label: 'Stato referto', type: 'select', options: REPORT_STATUS_OPTIONS},
]

const REPORT_FILE_NAME = 'referto.pdf'
const REPORT_MIME = 'application/pdf'
const INITIAL_VIEWER_STATE = {open: false, filePath: '', fileName: REPORT_FILE_NAME, status: STATUS.PENDING}

// Same success/warning convention used for enum-like statuses elsewhere in pages-new
// (see StatusPill usage in PatientsListNew and SurveysTab): completed -> success, pending -> warning.
function getExamStateColor(state: string | undefined): 'success' | 'warning' | 'default' {
  if (state === 'Completato') return 'success'
  if (state === 'Assente') return 'warning'
  return 'default'
}

function renderCell(id: string) {
  return (row: ExamRow): React.ReactNode => {
    if (id === 'date') {
      return <Typography variant="body2">{row.date ? moment(row.date).format('ddd DD MMM YYYY HH:mm') : '—'}</Typography>
    }
    if (id === 'type') {
      const label = row.type === 'eco' ? 'ECO' : row.type
      return <Typography variant="body2">{label ?? '—'}</Typography>
    }
    if (id === 'state') {
      return <StatusPill label={row.state ?? '—'} color={getExamStateColor(row.state)} />
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

/**
 * Each exam row can expose up to two independent actions, shown conditionally:
 * a report download/preview (when unsignedReportId or signedReportId is present)
 * and a DICOM viewer link (when studyUID is present). Unlike DossierTab, the
 * report preview always opens as a PDF — there is no force-download branch.
 */
export default function ExamsTab({patientId}: ExamsTabProps): JSX.Element {
  const examsColumnOptions: ExamColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.examsConfiguration?.columnOptions
  )

  const [rows, setRows] = useState<ExamRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [statusDownload, setStatusDownload] = useState<string | undefined>()
  const [statusViewer, setStatusViewer] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-date'})
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getExams(patientId, filters, setStatus, pagination),
      apiClient.getExamsCount(patientId, filters, setStatusCount),
    ])
      .then(([exams, countResult]) => {
        if (Array.isArray(exams)) {
          setRows(exams.map((exam: any) => ({
            ...exam,
            reportingDoctor: exam.reportingDoctor && `${exam.reportingDoctor.firstName} ${exam.reportingDoctor.lastName}`,
            requestingUser: exam.requestingUser && `${exam.requestingUser.firstName} ${exam.requestingUser.lastName}`,
            state: exam?.signedReportId ? 'Completato' : 'Assente',
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  // Belt-and-suspenders: `pagination.order` is sent to the backend via getExams, but also
  // re-sort the already-fetched page client-side so the "DATA ESAME" arrow always visibly
  // reorders rows even if the API/mock ignores the `sort` param.
  const sortedRows = useMemo(() => {
    const order = pagination.order
    if (!order || (order !== 'date' && order !== '-date')) return rows
    const desc = order.startsWith('-')
    return [...rows].sort((a, b) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0
      const bTime = b.date ? new Date(b.date).getTime() : 0
      return desc ? bTime - aTime : aTime - bTime
    })
  }, [rows, pagination.order])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleDownloadReport = async (row: ExamRow) => {
    setViewerState({open: true, filePath: '', fileName: REPORT_FILE_NAME, status: STATUS.PENDING})
    const fileResponse = await apiClient.downloadMedicalReport(patientId, row._id, setStatusDownload)
    if (!fileResponse) return
    const blob = new Blob([new Uint8Array(fileResponse)], {type: REPORT_MIME})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState({open: true, filePath: url, fileName: REPORT_FILE_NAME, status: STATUS.FULFILLED})
  }

  const handleOpenDicomViewer = async (row: ExamRow) => {
    const response = await apiClient.getDicomToken(patientId, row._id, row.studyUID as string, setStatusViewer)
    if (!response?.token) return
    window.open(`https://view.miacare.zeeromed.cloud/ngv/?q=${response.token}`, '_blank')
  }

  const columnConfig = examsColumnOptions?.length > 0 ? examsColumnOptions : DEFAULT_COLUMNS

  const filterConfig: FilterItem[] = useMemo(() => {
    const visibleColumnIds = new Set(columnConfig.map(col => col.id))
    return ALL_FILTERS
      .filter(({columnId}) => visibleColumnIds.has(columnId))
      .map(({columnId, ...filterItem}) => filterItem)
  }, [columnConfig])

  const columns: ColumnDef<ExamRow>[] = [
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      sortKey: col.id === 'date' ? 'date' : undefined,
      render: renderCell(col.id),
    })),
    {
      key: 'actions',
      header: '',
      width: 80,
      isActionColumn: true,
      render: (row: ExamRow) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          {(row.unsignedReportId || row.signedReportId) && (
            <ActionIconButton variant="download" tooltip="Scarica referto" onClick={() => handleDownloadReport(row)} />
          )}
          {row.studyUID && (
            <ActionIconButton variant="openExternal" tooltip="Apri viewer DICOM" onClick={() => handleOpenDicomViewer(row)} />
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nello scaricare il referto.'}
        </InlineMessage>
      )}

      {statusViewer === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusViewer(undefined)} sx={{mb: 2}}>
          {"Errore nel richiedere l'esame."}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      <DataTable<ExamRow>
        columns={columns}
        rows={sortedRows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun esame trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={REPORT_MIME}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
